import { LINK_TYPE } from '@/constants/link';
import Contact from '@/classes/v2/Contact';
import store from '@/store';

/**
 * Group Class.
 *
 * @typedef {Object} Group
 * @typedef {Object} Contact
 *
 * @module Group
 **/
export default class Group {

  /**
   * @constructor
   * @this {Group}
   *
   * @param {Object} RawGroup - Group information.
   **/
  constructor({ active, contacts, groupName, id, linkId, linkType }) {
    this.id = id;

    this.groupName = groupName;
    this.contactsNumbers = (contacts || []).map(contact => contact.number);

    this.active = active;
    this.linkId = linkId;
    this.linkType = linkType;
  }

  /**
   * Public getters
   */

  /**
   * Returns an alphabetically sorted list of contacts.
   *
   * Ascending order.
   *
   * @returns {Array<Contact>}
   */
  get contacts() {
    return this.contactsNumbers
      .map(number => store.getters['contactsV2/getContactByNumber'](number))
      .filter(contact => contact)
      .sort(Contact.compare);
  }

  /**
   * Determines whether the Group is managed by the enterprise or not,
   * returning true or false as appropriate.
   *
   * @returns {Boolean}
   */
  get isEnterpriseGroup() {
    return this.linkType === LINK_TYPE.account;
  }

  /**
   * Determines whether the Group is managed by the current user or not,
   * returning true or false as appropriate.
   *
   * @returns {Boolean}
   */
  get isUserGroup() {
    return this.linkType === LINK_TYPE.user;
  }

  /**
   * Returns a clean/lean version of the group information.
   *
   * @returns {Object}
   */
  get lean() {
    return {
      id: this.id,
      groupName: this.groupName,
      contacts: this.contacts.map(contact => contact.lean),
      active: this.active,
      linkId: this.linkId,
      linkType: this.linkType
    }
  }

  /**
   * Public methods
   */

  /**
   * Determines whether one string may be found within comparable Group fields,
   * returning true or false as appropriate.
   *
   * @param {String} searchString
   *
   * @returns {Boolean}
   */
  includes(searchString) {
    const comparableFields = [this.groupName].join().toUpperCase();
    return comparableFields.includes((searchString || '').toUpperCase());
  }

  /**
   * Returns a filtered & sorted list of Contact objects containing a given searchString.
   *
   * @param {String} searchString
   *
   * @returns {Array<Contact>}
   */
  filteredContacts(searchString) {
    return this.contacts
      .filter(contact => contact?.includes(searchString))
      .sort(Contact.compare);
  }

  /**
   * Public static methods
   */

  /**
   * Compares two given Group objects for order.
   * Returns a negative integer, zero, or a positive integer as GroupA object is less than,
   * equal to, or greater than GroupB object.
   *
   * @param {Group} groupA
   * @param {Group} groupB
   *
   * @returns {Number}
   */
  static compare(groupA, groupB) {
    const comparableFieldsA = [groupA.groupName.toUpperCase()].join('');
    const comparableFieldsB = [groupB.groupName.toUpperCase()].join('');

    if (comparableFieldsA < comparableFieldsB) return -1;
    if (comparableFieldsA > comparableFieldsB) return 1;
    return 0;
  }
}

